import React, { useState } from 'react';
import { db, generateLocalId } from './db';
import { Plus, Trash2, Edit2, ChevronRight, ChevronDown, Folder, FolderOpen } from 'lucide-react';

const LocationManager = ({ tree, refreshTree, darkMode }) => {
  const [expanded, setExpanded] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [addingChildTo, setAddingChildTo] = useState(null); // id or 'root'
  const [newName, setNewName] = useState('');

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAdd = async (parentId) => {
    if (!newName.trim()) return;
    try {
      const newId = generateLocalId('local_loc_');
      
      // In-memory filter to prevent equals(null) IndexedDB crashes
      const allLocs = await db.locations.toArray();
      const locs = allLocs.filter(l => l.parent_id === (parentId || null) && l.sync_status !== 'pending_delete');
      
      const existing = locs.find(l => l.name.toLowerCase() === newName.trim().toLowerCase());
      if (existing) {
        alert('해당 경로에 동일한 이름의 위치가 이미 존재합니다.');
        return;
      }

      await db.locations.put({
        id: newId,
        name: newName.trim(),
        parent_id: parentId || null,
        sync_status: 'pending_upload',
        updated_at: new Date().toISOString()
      });

      setNewName('');
      setAddingChildTo(null);
      refreshTree();
    } catch (error) {
      console.error("Error adding location locally:", error);
    }
  };

  const handleUpdate = async (id, parentId) => {
    if (!editName.trim()) return;
    try {
      // In-memory filter to prevent equals(null) IndexedDB crashes
      const allLocs = await db.locations.toArray();
      const locs = allLocs.filter(l => l.parent_id === (parentId || null) && l.sync_status !== 'pending_delete');
      
      const existing = locs.find(l => l.id !== id && l.name.toLowerCase() === editName.trim().toLowerCase());
      if (existing) {
        alert('동일한 이름의 위치가 이미 존재합니다.');
        return;
      }

      await db.locations.update(id, {
        name: editName.trim(),
        sync_status: 'pending_upload',
        updated_at: new Date().toISOString()
      });

      setEditingId(null);
      refreshTree();
    } catch (error) {
      console.error("Error updating location locally:", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("정말 삭제하시겠습니까? 하위 위치나 물품이 있으면 삭제할 수 없습니다.")) return;
    try {
      // Check children
      const allLocs = await db.locations.toArray();
      const children = allLocs.filter(l => l.parent_id === id && l.sync_status !== 'pending_delete');
      if (children.length > 0) {
        alert("삭제 실패: 하위 위치가 포함되어 있습니다.");
        return;
      }

      // Check items
      const allItems = await db.items.toArray();
      const items = allItems.filter(i => i.location_id === id && i.sync_status !== 'pending_delete');
      if (items.length > 0) {
        alert("삭제 실패: 이 위치에 등록된 물품이 존재합니다.");
        return;
      }

      if (String(id).startsWith('local_')) {
        await db.locations.delete(id);
      } else {
        await db.locations.update(id, {
          sync_status: 'pending_delete',
          updated_at: new Date().toISOString()
        });
      }
      refreshTree();
    } catch (error) {
      console.error("Error deleting location locally:", error);
    }
  };

  const LocationNode = ({ node, level = 0 }) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expanded[node.id];
    const isEditing = editingId === node.id;

    return (
      <div style={{ marginLeft: level * 20 }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          padding: '8px', 
          borderBottom: darkMode ? '1px solid #333' : '1px solid #eee',
          backgroundColor: darkMode ? '#2a2a2a' : '#fff',
          color: darkMode ? '#eee' : '#333'
        }}>
          <div style={{ cursor: 'pointer', marginRight: '5px' }} onClick={() => toggleExpand(node.id)}>
            {hasChildren ? (isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : <span style={{ width: 16, display: 'inline-block' }}></span>}
          </div>
          
          <div style={{ marginRight: '10px', color: '#0088FE' }}>
             {hasChildren ? <FolderOpen size={16} /> : <Folder size={16} />}
          </div>

          {isEditing ? (
            <div style={{ flex: 1, display: 'flex', gap: '5px' }}>
              <input 
                value={editName} 
                onChange={(e) => setEditName(e.target.value)}
                style={{
                  padding: '5px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  flex: 1
                }}
              />
              <button onClick={() => handleUpdate(node.id, node.parent_id)} style={styles.actionBtn}>저장</button>
              <button onClick={() => setEditingId(null)} style={styles.cancelBtn}>취소</button>
            </div>
          ) : (
            <div style={{ flex: 1 }}>{node.name}</div>
          )}

          {!isEditing && (
            <div style={{ display: 'flex', gap: '5px' }}>
              <button onClick={() => { setAddingChildTo(node.id); setNewName(''); }} style={styles.iconBtn} title="하위 위치 추가">
                <Plus size={14} />
              </button>
              <button onClick={() => { setEditingId(node.id); setEditName(node.name); }} style={styles.iconBtn} title="수정">
                <Edit2 size={14} />
              </button>
              <button onClick={() => handleDelete(node.id)} style={{...styles.iconBtn, color: '#ff4d4d'}} title="삭제">
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>

        {addingChildTo === node.id && (
          <div style={{ marginLeft: 20, padding: '10px', display: 'flex', gap: '5px' }}>
            <input 
              value={newName} 
              onChange={(e) => setNewName(e.target.value)}
              placeholder="새 하위 위치 이름"
              style={{
                padding: '5px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                flex: 1
              }}
              autoFocus
            />
            <button onClick={() => handleAdd(node.id)} style={styles.actionBtn}>추가</button>
            <button onClick={() => setAddingChildTo(null)} style={styles.cancelBtn}>취소</button>
          </div>
        )}

        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => (
              <LocationNode key={child.id} node={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h2 style={{ color: darkMode ? '#eee' : '#333', marginBottom: '20px' }}>위치(공간) 관리</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={() => { setAddingChildTo('root'); setNewName(''); }} 
          style={{
            ...styles.actionBtn,
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
        >
          <Plus size={16} /> 최상위 위치 추가
        </button>
      </div>

      {addingChildTo === 'root' && (
        <div style={{ padding: '10px', display: 'flex', gap: '5px', marginBottom: '10px' }}>
          <input 
            value={newName} 
            onChange={(e) => setNewName(e.target.value)}
            placeholder="새 위치 이름 (예: 집, 사무실)"
            style={{
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              flex: 1
            }}
            autoFocus
          />
          <button onClick={() => handleAdd(null)} style={styles.actionBtn}>추가</button>
          <button onClick={() => setAddingChildTo(null)} style={styles.cancelBtn}>취소</button>
        </div>
      )}

      <div style={{ 
        border: darkMode ? '1px solid #444' : '1px solid #ddd', 
        borderRadius: '8px', 
        overflow: 'hidden',
        backgroundColor: darkMode ? '#1e1e1e' : '#fff'
      }}>
        {tree.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
            등록된 위치가 없습니다.
          </div>
        ) : (
          tree.map(node => (
            <LocationNode key={node.id} node={node} />
          ))
        )}
      </div>
    </div>
  );
};

const styles = {
  actionBtn: {
    backgroundColor: '#0088FE',
    color: '#fff',
    border: 'none',
    padding: '5px 10px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px'
  },
  cancelBtn: {
    backgroundColor: '#888',
    color: '#fff',
    border: 'none',
    padding: '5px 10px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px'
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '5px',
    color: '#666',
    display: 'flex',
    alignItems: 'center'
  }
};

export default LocationManager;
